import React, { useEffect, useMemo, useState } from 'react';
import { Building2, Loader2, Lock, Mail, MessageSquareText, Search, Users, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ModalOverlay from '../ui/ModalOverlay';
import {
  getShareSettings,
  listShareMembers,
  lookupShareRecipient,
  type ShareMember,
  type SharePerson,
  type ShareSettings,
} from '../../lib/canvasShare';

type Props = {
  open: boolean;
  orgId: string;
  projectId: string;
  projectTitle: string;
  /** The organization's plan has a team (Empresarial). */
  canShare: boolean;
  /** Current chat has at least one message worth sharing. */
  hasChat?: boolean;
  onClose: () => void;
  /** Saves the sharing; resolves when the shared copy is up to date. */
  onSave: (settings: ShareSettings) => Promise<void>;
};

type Access = 'view' | 'edit';
type PersonRow = SharePerson & { access: Access };

export default function CanvasShareModal(props: Props) {
  const { open, orgId, projectId, canShare, hasChat } = props;
  const navigate = useNavigate();
  const [members, setMembers] = useState<ShareMember[]>([]);
  const [people, setPeople] = useState<Map<string, PersonRow>>(new Map());
  const [shareAll, setShareAll] = useState(false);
  const [allAccess, setAllAccess] = useState<Access>('view');
  const [shareChat, setShareChat] = useState(false);
  const [wasShared, setWasShared] = useState(false);
  const [query, setQuery] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [emailAccess, setEmailAccess] = useState<Access>('view');
  const [lookingUp, setLookingUp] = useState(false);
  const [pendingExternal, setPendingExternal] = useState<PersonRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !canShare || !orgId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setQuery('');
    setEmailInput('');
    setPendingExternal(null);
    Promise.all([listShareMembers(orgId), getShareSettings(projectId)])
      .then(([list, settings]) => {
        if (cancelled) return;
        setMembers(list);
        setWasShared(Boolean(settings));
        setShareAll(Boolean(settings?.shareAll));
        setAllAccess(settings?.shareAllCanEdit ? 'edit' : 'view');
        setShareChat(Boolean(settings?.shareChat));
        const next = new Map<string, PersonRow>();
        for (const p of settings?.people || []) {
          next.set(p.userId, {
            ...p,
            access: p.canEdit ? 'edit' : 'view',
            name: p.name || list.find((m) => m.userId === p.userId)?.name || p.email || 'Pessoa',
            email: p.email || list.find((m) => m.userId === p.userId)?.email || '',
          });
        }
        setPeople(next);
      })
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : 'Falha ao carregar a equipe.'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, canShare, orgId, projectId]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? members.filter((m) => `${m.name} ${m.email}`.toLowerCase().includes(q)) : members;
  }, [members, query]);

  const extras = useMemo(
    () => Array.from(people.values()).filter((p) => p.external || !members.some((m) => m.userId === p.userId)),
    [people, members],
  );

  if (!open) return null;

  const setPerson = (userId: string, access: Access | null, meta?: Partial<PersonRow>) => {
    setPeople((cur) => {
      const next = new Map(cur);
      if (!access) {
        next.delete(userId);
        return next;
      }
      const prev = next.get(userId);
      next.set(userId, {
        userId,
        access,
        canEdit: access === 'edit',
        email: meta?.email ?? prev?.email,
        name: meta?.name ?? prev?.name,
        external: meta?.external ?? prev?.external,
      });
      return next;
    });
  };

  const addRecipient = async () => {
    const email = emailInput.trim();
    if (!email || lookingUp) return;
    setLookingUp(true);
    setError(null);
    try {
      const hit = await lookupShareRecipient(orgId, email);
      if (!hit) {
        setError('Não há conta do Seu.video com esse e-mail.');
        return;
      }
      const row: PersonRow = {
        userId: hit.userId,
        email: hit.email,
        name: hit.name,
        access: emailAccess,
        canEdit: emailAccess === 'edit',
        external: hit.external,
      };
      if (hit.external) {
        setPendingExternal(row);
        return;
      }
      setPerson(hit.userId, emailAccess, row);
      setEmailInput('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível buscar esse e-mail.');
    } finally {
      setLookingUp(false);
    }
  };

  const confirmExternal = () => {
    if (!pendingExternal) return;
    setPerson(pendingExternal.userId, pendingExternal.access, pendingExternal);
    setEmailInput('');
    setPendingExternal(null);
  };

  const save = async (settings: ShareSettings) => {
    setSaving(true);
    setError(null);
    try {
      await props.onSave(settings);
      props.onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao compartilhar.');
    } finally {
      setSaving(false);
    }
  };

  const settings: ShareSettings = {
    shareAll,
    shareAllCanEdit: allAccess === 'edit',
    shareChat: shareChat && Boolean(hasChat),
    people: Array.from(people.values()).map((p) => ({
      userId: p.userId,
      canEdit: p.access === 'edit',
      email: p.email,
      name: p.name,
      external: p.external,
    })),
  };
  const nothing = !shareAll && people.size === 0;

  return (
    <ModalOverlay onClose={saving ? undefined : props.onClose}>
      <div className="tb-share-card" role="dialog" aria-modal="true" aria-label="Compartilhar projeto" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="tb-share-close" aria-label="Fechar" onClick={props.onClose} disabled={saving}>
          <X size={16} />
        </button>
        <h2 className="tb-share-title"><Users size={17} /> Compartilhar “{props.projectTitle}”</h2>
        <p className="tb-share-sub">
          Pessoas da sua empresa ou outra conta do Seu.video. Quem recebe vê o quadro e o roteiro; a conversa do agente só vai junto se você marcar abaixo. Um e-mail avisa cada pessoa adicionada.
        </p>

        {!canShare ? (
          <div className="tb-share-locked">
            <Lock size={18} />
            <div>
              <strong>Disponível no plano Empresarial</strong>
              <span>Compartilhe projetos com a equipe, com permissão de ver ou editar para cada pessoa.</span>
            </div>
            <button type="button" className="tb-share-primary" onClick={() => navigate('/pagamento')}>
              Ver planos
            </button>
          </div>
        ) : loading ? (
          <div className="tb-share-loading"><Loader2 size={16} className="spin" /> Carregando a equipe…</div>
        ) : (
          <>
            <label className={`tb-share-row tb-share-all${shareAll ? ' is-on' : ''}`}>
              <input type="checkbox" checked={shareAll} onChange={(e) => setShareAll(e.target.checked)} />
              <span className="tb-share-avatar is-org"><Building2 size={14} /></span>
              <span className="tb-share-who">
                <strong>Toda a empresa</strong>
                <small>{members.length} {members.length === 1 ? 'pessoa' : 'pessoas'} além de você</small>
              </span>
              <AccessSelect value={allAccess} disabled={!shareAll} onChange={setAllAccess} />
            </label>

            <div className="tb-share-email">
              <Mail size={14} />
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void addRecipient(); } }}
                placeholder="E-mail de alguém do Seu.video"
                disabled={lookingUp || saving}
              />
              <AccessSelect value={emailAccess} disabled={lookingUp || saving} onChange={setEmailAccess} />
              <button type="button" className="tb-share-ghost" disabled={lookingUp || saving || !emailInput.trim()} onClick={() => void addRecipient()}>
                {lookingUp ? <Loader2 size={14} className="spin" /> : 'Adicionar'}
              </button>
            </div>

            {members.length > 6 && (
              <div className="tb-share-search">
                <Search size={13} />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar por nome ou e-mail" />
              </div>
            )}

            <div className="tb-share-list">
              {!members.length && !extras.length && (
                <div className="tb-share-empty">Ainda não há outras pessoas na empresa. Adicione um e-mail acima ou convide a equipe em Usuários.</div>
              )}
              {extras.map((p) => (
                <label key={p.userId} className="tb-share-row is-on">
                  <input type="checkbox" checked onChange={() => setPerson(p.userId, null)} />
                  <span className="tb-share-avatar is-external">{(p.name || p.email || '?').slice(0, 1).toUpperCase()}</span>
                  <span className="tb-share-who">
                    <strong>{p.name || p.email}</strong>
                    <small>{p.email}{p.external ? ' · fora da empresa' : ''}</small>
                  </span>
                  <AccessSelect value={p.access} onChange={(v) => setPerson(p.userId, v, p)} />
                </label>
              ))}
              {visible.map((m) => {
                const access = people.get(m.userId)?.access;
                return (
                  <label key={m.userId} className={`tb-share-row${access ? ' is-on' : ''}`}>
                    <input type="checkbox" checked={Boolean(access)} onChange={(e) => setPerson(m.userId, e.target.checked ? (shareAll ? allAccess : 'view') : null, { email: m.email, name: m.name, external: false })} />
                    <span className="tb-share-avatar">{(m.name || m.email).slice(0, 1).toUpperCase()}</span>
                    <span className="tb-share-who">
                      <strong>{m.name}</strong>
                      {m.email && m.email !== m.name && <small>{m.email}</small>}
                    </span>
                    <AccessSelect
                      value={access || (shareAll ? allAccess : 'view')}
                      disabled={!access}
                      onChange={(v) => setPerson(m.userId, v, { email: m.email, name: m.name, external: false })}
                    />
                  </label>
                );
              })}
            </div>
            {shareAll && people.size > 0 && (
              <p className="tb-share-note">A permissão marcada numa pessoa vale mais que a de “Toda a empresa”.</p>
            )}

            <label className={`tb-share-row tb-share-chat${shareChat ? ' is-on' : ''}${!hasChat ? ' is-disabled' : ''}`}>
              <input
                type="checkbox"
                checked={shareChat}
                disabled={!hasChat}
                onChange={(e) => setShareChat(e.target.checked)}
              />
              <span className="tb-share-avatar is-chat"><MessageSquareText size={14} /></span>
              <span className="tb-share-who">
                <strong>Incluir conversa do agente</strong>
                <small>
                  {hasChat
                    ? 'Quem abrir o projeto vê o histórico do chat junto com o quadro.'
                    : 'Ainda não há mensagens neste projeto para compartilhar.'}
                </small>
              </span>
            </label>
          </>
        )}

        {error && <p className="tb-share-error">{error}</p>}

        {canShare && !loading && (
          <div className="tb-share-actions">
            {wasShared && (
              <button
                type="button"
                className="tb-share-ghost is-danger"
                disabled={saving}
                onClick={() => void save({ shareAll: false, shareAllCanEdit: false, people: [], shareChat: false })}
              >
                Parar de compartilhar
              </button>
            )}
            <button type="button" className="tb-share-primary" disabled={saving || (nothing && !wasShared)} onClick={() => void save(settings)}>
              {saving ? <Loader2 size={14} className="spin" /> : null}
              {nothing && wasShared ? 'Salvar (deixa de compartilhar)' : wasShared ? 'Salvar' : 'Compartilhar'}
            </button>
          </div>
        )}

        {pendingExternal && (
          <div className="tb-share-confirm" role="alertdialog" aria-label="Confirmar pessoa fora da empresa">
            <h3>Pessoa fora da sua empresa</h3>
            <p>
              <strong>{pendingExternal.name || pendingExternal.email}</strong>
              {pendingExternal.email ? ` (${pendingExternal.email})` : ''} não faz parte da sua organização.
              O projeto será compartilhado mesmo assim, e a pessoa receberá um e-mail com o link.
            </p>
            <div className="tb-share-actions">
              <button type="button" className="tb-share-ghost" onClick={() => setPendingExternal(null)}>Cancelar</button>
              <button type="button" className="tb-share-primary" onClick={confirmExternal}>Compartilhar mesmo assim</button>
            </div>
          </div>
        )}
      </div>
    </ModalOverlay>
  );
}

function AccessSelect({ value, disabled, onChange }: { value: Access; disabled?: boolean; onChange: (v: Access) => void }) {
  return (
    <select
      className="tb-share-access"
      value={value}
      disabled={disabled}
      onClick={(e) => e.preventDefault()}
      onChange={(e) => onChange(e.target.value as Access)}
    >
      <option value="view">Pode ver</option>
      <option value="edit">Pode editar</option>
    </select>
  );
}
